import type { Box, Size } from "./closed-frame-extent";

/** Padding (px) around each surface for its drop shadow (up to 0 10px 28px) and hover lift. */
export const HIT_PAD = 24;

/** A frame-relative rectangle (whole px) the closed iframe keeps clickable. */
export interface HitRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** A visible surface of the closed widget, optionally cut to its (scrolling) container. */
export interface HitSurface {
  box: Box;
  within?: Box;
}

/**
 * The parts of the closed iframe that must stay clickable (and painted).
 *
 * The closed frame is a rectangle big enough for the launcher, the peek and its quick replies,
 * and every transparent pixel of it swallows host-page clicks. The loader clips the frame to
 * these rects (`clip-path` clips hit-testing as well as painting), so the host page stays
 * clickable between and around the widget's surfaces. Each surface is cut to its container
 * first (a quick reply scrolled out of the peek has no hit area), moved by `shift` (the frame is
 * about to be resized: `viewport` is its NEW size), padded by `pad` for its drop shadow and hover
 * lift, and clamped to that viewport.
 */
export function hitRects(
  surfaces: HitSurface[],
  pad: number,
  viewport: Size,
  shift: { x: number; y: number } = { x: 0, y: 0 },
): HitRect[] {
  const rects: HitRect[] = [];
  for (const { box, within } of surfaces) {
    let left = box.left;
    let top = box.top;
    let right = box.left + box.width;
    let bottom = box.top + box.height;
    if (within) {
      left = Math.max(left, within.left);
      top = Math.max(top, within.top);
      right = Math.min(right, within.left + within.width);
      bottom = Math.min(bottom, within.top + within.height);
    }
    left += shift.x;
    right += shift.x;
    top += shift.y;
    bottom += shift.y;
    if (right <= left || bottom <= top) continue;
    const x = Math.max(0, Math.floor(left - pad));
    const y = Math.max(0, Math.floor(top - pad));
    const r = Math.min(viewport.width, Math.ceil(right + pad));
    const b = Math.min(viewport.height, Math.ceil(bottom + pad));
    if (r > x && b > y) rects.push({ x, y, w: r - x, h: b - y });
  }
  return rects;
}
